import { Link } from "react-router-dom";
import { COMPANY } from "../data/content";
import MapComponent from "./MapComponent";
import ContactForm from "./ContactForm";

export default function Footer() {
  return (
    <footer className="bg-forest-950 text-sand-100 pt-16 pb-8 border-t border-forest-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        
        {/* TWO-COLUMN CONTACT LAYOUT */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
          
          {/* Column 1: Map Embed (Full height & zoomed out over Jutland) */}
          <div className="rounded-2xl overflow-hidden border border-forest-800 shadow-lg flex flex-col h-full min-h-[380px] lg:min-h-[460px]">
            <div className="bg-forest-900/90 px-5 py-3 border-b border-forest-800 flex items-center justify-between text-xs shrink-0">
              <span className="font-bold text-white uppercase tracking-wider flex items-center gap-1.5">
                <i className="fa-solid fa-map-location-dot text-emerald-400"></i> Dækningsområde Jylland
              </span>
              <span className="text-emerald-300 font-medium">Udkørende</span>
            </div>
            <div className="flex-1 w-full h-full">
              <MapComponent fullHeight={true} />
            </div>
          </div>

          {/* Column 2: Contact Information */}
          <div className="bg-forest-900/60 p-6 sm:p-8 rounded-2xl border border-forest-800 flex flex-col justify-between space-y-6 h-full">
            <div className="space-y-4">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-emerald-400 block mb-1">
                  Kontaktoplysninger
                </span>
                <h3 className="text-2xl font-bold text-white tracking-tight">
                  Hoofcare
                </h3>
                <p className="text-xs text-sand-200/80 mt-1">
                  Professionel og skånsom naturlig hovtrimning af heste.
                </p>
              </div>

              {/* 3 Detail Blocks */}
              <div className="space-y-4 pt-2">
                {/* Block 1: Telefon */}
                <div className="flex items-start gap-3.5 p-3.5 rounded-xl bg-forest-950/40 border border-forest-800/80">
                  <div className="w-10 h-10 rounded-lg bg-forest-800 text-emerald-400 flex items-center justify-center shrink-0 text-base">
                    <i className="fa-solid fa-phone"></i>
                  </div>
                  <div>
                    <span className="block text-[11px] font-bold uppercase tracking-wider text-sand-300">
                      Telefon / SMS
                    </span>
                    <a
                      href={`tel:${COMPANY.phoneRaw}`}
                      className="text-white font-semibold text-sm sm:text-base hover:text-emerald-300 transition-colors whitespace-nowrap block"
                    >
                      <span className="whitespace-nowrap">{COMPANY.phone}</span>
                    </a>
                  </div>
                </div>

                {/* Block 2: Email */}
                <div className="flex items-start gap-3.5 p-3.5 rounded-xl bg-forest-950/40 border border-forest-800/80">
                  <div className="w-10 h-10 rounded-lg bg-forest-800 text-emerald-400 flex items-center justify-center shrink-0 text-base">
                    <i className="fa-solid fa-envelope"></i>
                  </div>
                  <div>
                    <span className="block text-[11px] font-bold uppercase tracking-wider text-sand-300">
                      E-mailadresse
                    </span>
                    <a
                      href={`mailto:${COMPANY.email}`}
                      className="text-white font-semibold text-sm sm:text-base hover:text-emerald-300 transition-colors whitespace-nowrap block"
                    >
                      <span className="whitespace-nowrap">{COMPANY.email}</span>
                    </a>
                  </div>
                </div>

                {/* Block 3: Adresse */}
                <div className="flex items-start gap-3.5 p-3.5 rounded-xl bg-forest-950/40 border border-forest-800/80">
                  <div className="w-10 h-10 rounded-lg bg-forest-800 text-emerald-400 flex items-center justify-center shrink-0 text-base">
                    <i className="fa-solid fa-location-dot"></i>
                  </div>
                  <div>
                    <span className="block text-[11px] font-bold uppercase tracking-wider text-sand-300">
                      Adresse & CVR
                    </span>
                    <p className="text-white font-semibold text-sm sm:text-base">
                      {COMPANY.address}, {COMPANY.city}
                    </p>
                    <p className="text-xs text-sand-300 mt-0.5">
                      CVR-nr: <strong className="text-white">{COMPANY.cvr}</strong>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Social & Insurance */}
            <div className="pt-4 border-t border-forest-800/80 flex items-center justify-between text-xs">
              <a
                href={COMPANY.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-blue-300 hover:text-white font-medium transition-colors"
              >
                <i className="fa-brands fa-facebook text-base"></i>
                <span>Følg os på Facebook</span>
              </a>
              <span className="text-sand-300/80">
                <i className="fa-solid fa-shield-halved text-emerald-400 mr-1"></i> If Forsikring
              </span>
            </div>
          </div>

        </div>

        {/* QUICK LINKS & COPYRIGHT */}
        <div className="pt-8 border-t border-forest-900/80 flex flex-col md:flex-row items-center justify-between gap-6 text-xs text-sand-300/80">
          <ul className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <li>
              <Link to="/" className="hover:text-white transition-colors cursor-pointer">Forside</Link>
            </li>
            <li>
              <Link to="/hovtrim" className="hover:text-white transition-colors cursor-pointer">Hovtrim</Link>
            </li>
            <li>
              <Link to="/faq" className="hover:text-white transition-colors cursor-pointer">FAQ</Link>
            </li>
            <li>
              <Link to="/om-mig" className="hover:text-white transition-colors cursor-pointer">Om mig</Link>
            </li>
            <li>
              <Link to="/isnhcp" className="hover:text-white transition-colors cursor-pointer">ISNHCP</Link>
            </li>
            <li>
              <Link to="/vandrefolde" className="hover:text-white transition-colors cursor-pointer">Vandrefolde</Link>
            </li>
            <li>
              <Link to="/priser" className="hover:text-white transition-colors cursor-pointer">Priser</Link>
            </li>
            <li>
              <Link to="/kontakt" className="hover:text-white transition-colors cursor-pointer">Kontakt</Link>
            </li>
          </ul>

          <div className="flex items-center gap-4 text-sand-300/60">
            <p>© {new Date().getFullYear()} {COMPANY.name}. CVR: {COMPANY.cvr}</p>
          </div>
        </div>

      </div>
    </footer>
  );
}
